import type { SidebarEntry, SidebarCustomer } from "@/api/sidebarEntryAPI";
import SidebarUser from "./SidebarUser";
import type { UseQueryResult } from "@tanstack/react-query";
import { cn } from "frontend-components";
import { useTranslation } from "react-i18next";
import SidebarNavigationEntryLink from "./SidebarNavigationEntryLink";
import SidebarPulseEntry, { type PulseRouteEntry } from "./SidebarPulseEntry";
import SidebarDashboardEntry from "./SidebarDashboardEntry";

function isPulseRouteEntry(entry: SidebarEntry): entry is PulseRouteEntry {
    return entry.type === "PULSE" && !entry.isUpsell && entry.navigation?.type === "PULSE";
}

type DashboardRouteEntry = Extract<SidebarEntry, { type: "DASHBOARD" }> & {
    navigation: Extract<NonNullable<SidebarEntry["navigation"]>, { type: "DASHBOARD" }>;
};

function isDashboardRouteEntry(entry: SidebarEntry): entry is DashboardRouteEntry {
    return entry.type === "DASHBOARD";
}

type SidebarData = {
    selectedCustomer: SidebarCustomer;
    availableCustomers: SidebarCustomer[];
    entries: SidebarEntry[];
};

export default function Sidebar({
    sidebarQuery,
    sidebarCustomersQuery,
    className,
    id,
}: {
    sidebarQuery: UseQueryResult<SidebarData>;
    sidebarCustomersQuery: UseQueryResult<SidebarCustomer[]>;
    className?: string;
    id?: string;
}) {
    const { t } = useTranslation();
    const { data, isPending, isError } = sidebarQuery;
    const entries = data?.entries ?? [];
    const pulseEntries = entries.filter((entry) => entry.type === "PULSE");
    const pulseRouteEntries = entries.filter(isPulseRouteEntry);
    const singlePulseRouteEntry = pulseRouteEntries.length === 1 ? pulseRouteEntries[0] : null;
    const firstPulseEntryKey = pulseEntries[0]?.key;

    return (
        <aside
            id={id}
            className={cn(
                "border-border bg-background flex w-[248px] shrink-0 flex-col overflow-hidden border-r",
                className,
            )}
        >
            <div className="flex items-center justify-center p-4">
                <div className="text-foreground font-display truncate pb-1 text-[28px] leading-tight tracking-tight">
                    {t("common.compendia").toLowerCase()}
                </div>
            </div>
            <nav className="flex flex-1 flex-col overflow-y-auto pb-3">
                <div className="flex flex-col gap-0.5">
                    {isPending ? (
                        <div className="text-muted-foreground px-5 py-3 text-sm">
                            {t("common.loading")}
                        </div>
                    ) : isError ? (
                        <div className="text-destructive px-5 py-3 text-sm">
                            {t("hub.loadError")}
                        </div>
                    ) : (
                        entries.map((entry) => {
                            if (entry.type === "PULSE" && entry.key !== firstPulseEntryKey) {
                                return null;
                            }

                            if (entry.type === "PULSE") {
                                return (
                                    <SidebarPulseEntry
                                        key={entry.key}
                                        entry={entry}
                                        customerId={data.selectedCustomer.id}
                                        pulseRouteEntries={pulseRouteEntries}
                                        singlePulseRouteEntry={singlePulseRouteEntry}
                                    />
                                );
                            }
                            if (isDashboardRouteEntry(entry)) {
                                return (
                                    <SidebarDashboardEntry
                                        key={entry.key}
                                        entry={entry}
                                        customerId={data.selectedCustomer.id}
                                    />
                                );
                            }
                            return (
                                <SidebarNavigationEntryLink
                                    key={entry.key}
                                    entry={entry}
                                    customerId={data.selectedCustomer.id}
                                />
                            );
                        })
                    )}
                </div>
            </nav>
            <SidebarUser
                selectedCustomer={data?.selectedCustomer}
                availableCustomers={sidebarCustomersQuery.data ?? data?.availableCustomers ?? []}
            />
        </aside>
    );
}
